/**
 * DispatchVideoWallPage · v2.7.35 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Admin Console → Developer → Dispatch Video Wall
 *
 * Configurator for the wall-mounted command-center grid that renders
 * at /kiosk/wall. Picks a grid preset (2x2 / 3x2 / 3x3 / 4x2 / etc.)
 * + assigns a content module to each cell. The kiosk renderer uses
 * percentage-based / aspect-ratio CSS so the same config drives both
 * 1080p (1920×1080) and 4K (3840×2160) wall displays.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, MonitorPlay, ExternalLink, Save, Grid3x3, RefreshCw, User, Link2, Download, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

const GRID_DIMS = {
  "1x1": { cols: 1, rows: 1 },
  "2x1": { cols: 2, rows: 1 },
  "2x2": { cols: 2, rows: 2 },
  "3x2": { cols: 3, rows: 2 },
  "3x3": { cols: 3, rows: 3 },
  "4x2": { cols: 4, rows: 2 },
};

// v2.7.62+E45 · §3 · slots for the 55-45-quad topology renderer.
// Each slot is a semantic name; the frontend's QuadTopologyBody
// positions them into the correct 55/45 layout partition.
const QUAD_SLOTS_ORDER = ["map_1", "map_2", "map_3", "map_4", "radar", "warnings", "abf", "fleet", "completed"];
const QUAD_SLOT_LABELS = {
  map_1: "Upper Map 1 (top-left)",
  map_2: "Upper Map 2 (top-right)",
  map_3: "Upper Map 3 (bottom-left)",
  map_4: "Upper Map 4 (bottom-right)",
  radar: "Right column · Weather Radar (top)",
  warnings: "Right column · Warnings (bottom)",
  abf:  "Lower · Active/Booked/Future 72h",
  fleet: "Lower · Fleet Stream",
  completed: "Lower · Completed Last 20",
};
const QUAD_DEFAULTS = {
  map_1: { type: "zoned_live_map", zone: "Z1" },
  map_2: { type: "zoned_live_map", zone: "Z5" },
  map_3: { type: "zoned_live_map", zone: "Z6" },
  map_4: { type: "zoned_live_map", zone: "Z7" },
  radar: { type: "weather_radar" },
  warnings: { type: "warnings_top10" },
  abf: { type: "dispatch_active_booked_future" },
  fleet: { type: "fleet_stream" },
  completed: { type: "dispatch_completed" },
};

export default function DispatchVideoWallPage() {
  const [cfg, setCfg] = useState(null);
  const [grid, setGrid] = useState("2x2");
  const [title, setTitle] = useState("Dispatch Command Wall");
  const [showClock, setShowClock] = useState(true);
  const [showWeather, setShowWeather] = useState(true);
  const [cells, setCells] = useState([]);
  const [cellTypes, setCellTypes] = useState({});
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);

  // v2.7.62+E45 · multi-instance topology: which wall's config are we editing?
  const [wallId, setWallId] = useState("default");
  const [walls, setWalls] = useState([]);
  const [newWallId, setNewWallId] = useState("");

  const reload = useCallback(async (wid = wallId) => {
    try {
      const [cfgR, wallsR] = await Promise.all([
        adminApi.get("/admin/kiosk-wall/config", { params: { wall_id: wid } }),
        adminApi.get("/admin/kiosk-wall/walls"),
      ]);
      setCfg(cfgR.data);
      setGrid(cfgR.data.grid || "2x2");
      setTitle(cfgR.data.title || "Dispatch Command Wall");
      setShowClock(!!cfgR.data.show_clock);
      setShowWeather(!!cfgR.data.show_weather_strip);
      setCells(cfgR.data.cells || []);
      setCellTypes(cfgR.data.cell_types || {});
      setPresets(cfgR.data.presets || []);
      setWalls(wallsR.data?.walls || []);
    } catch (e) {
      toast.error(`Kiosk wall load failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, [wallId]);
  useEffect(() => { reload(); }, [reload]);

  const switchWall = async (wid) => {
    setWallId(wid);
    await reload(wid);
  };

  const createWall = async () => {
    const raw = (newWallId || "").trim();
    if (!raw) { toast.error("Wall ID required"); return; }
    // Normalize client-side (server also normalizes)
    const wid = raw.toLowerCase().replace(/[^a-z0-9\-_]/g, "-").slice(0, 64) || "default";
    if (walls.some((w) => w.wall_id === wid)) {
      toast.error(`Wall '${wid}' already exists`);
      return;
    }
    setNewWallId("");
    setWallId(wid);
    // Save initial DEFAULT config for the new wall
    try {
      await adminApi.put("/admin/kiosk-wall/config", {
        wall_id: wid,
        grid: "2x2",
        title: "Dispatch Command Wall",
        show_clock: true,
        show_weather_strip: true,
        cells: [
          { id: "c0", type: "live_map" },
          { id: "c1", type: "active_warnings" },
          { id: "c2", type: "weather_radar" },
          { id: "c3", type: "earnings_board" },
        ],
      });
      toast.success(`Wall '${wid}' created · editing now`);
      await reload(wid);
    } catch (e) {
      toast.error(`Create wall failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // v2.7.63+E46 · one-click clone current wall config to a new wall_id.
  // Great for standardizing a fleet: configure the 55-45-quad on
  // `default`, then Duplicate → east-wall / west-wall / dispatch-room.
  const duplicateWall = async () => {
    const raw = (newWallId || "").trim();
    if (!raw) { toast.error("Enter target wall ID first"); return; }
    const tgt = raw.toLowerCase().replace(/[^a-z0-9\-_]/g, "-").slice(0, 64) || "default";
    if (tgt === wallId) { toast.error("Source and target must differ"); return; }
    const alreadyExists = walls.some((w) => w.wall_id === tgt);
    if (alreadyExists && !window.confirm(`Wall '${tgt}' already exists. Overwrite with a fresh copy of '${wallId}'?`)) return;
    try {
      const r = await adminApi.post("/admin/kiosk-wall/config/copy", {
        source_wall_id: wallId,
        target_wall_id: tgt,
        overwrite: alreadyExists,
      });
      toast.success(`Copied ${r.data.cells_copied} cells: ${wallId} → ${tgt}`);
      setNewWallId("");
      setWallId(tgt);
      await reload(tgt);
    } catch (e) {
      toast.error(`Duplicate failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const deleteWall = async () => {
    if (wallId === "default") { toast.error("Cannot delete the default wall"); return; }
    if (!window.confirm(`Delete wall '${wallId}'? Any device provisioning-links pointing to it will fall back to 'default'. This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/kiosk-wall/config/${encodeURIComponent(wallId)}`);
      toast.success(`Deleted wall '${wallId}' · switched to default`);
      setWallId("default");
      await reload("default");
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const dims = GRID_DIMS[grid] || GRID_DIMS["2x2"];
  const expectedCount = dims.cols * dims.rows;
  const isQuad = grid === "55-45-quad";

  // When grid preset changes, resize the cells array
  const onGridChange = (g) => {
    setGrid(g);
    if (g === "55-45-quad") {
      // Populate the 9 semantic slots with sensible defaults
      setCells(QUAD_SLOTS_ORDER.map((slot) => ({
        id: `slot-${slot}`,
        slot,
        ...QUAD_DEFAULTS[slot],
      })));
      return;
    }
    const want = (GRID_DIMS[g] || GRID_DIMS["2x2"]).cols * (GRID_DIMS[g] || GRID_DIMS["2x2"]).rows;
    setCells((prev) => {
      // Drop any slot-addressed cells (from a prior quad config)
      const clean = prev.filter((c) => !c.slot).slice(0, want);
      while (clean.length < want) {
        clean.push({ id: `c${clean.length}`, type: "blank" });
      }
      return clean;
    });
  };

  const setCellType = (idx, type) => {
    setCells((prev) => prev.map((c, i) => (i === idx ? { ...c, type } : c)));
  };

  const setQuadSlotType = (slot, type) => {
    setCells((prev) => prev.map((c) => (c.slot === slot ? { ...c, type } : c)));
  };

  const setQuadSlotZone = (slot, zone) => {
    setCells((prev) => prev.map((c) => (c.slot === slot ? { ...c, zone: zone || undefined } : c)));
  };

  const save = async () => {
    if (!isQuad && cells.length !== expectedCount) {
      toast.error(`Grid ${grid} requires ${expectedCount} cells; got ${cells.length}`);
      return;
    }
    setBusy(true);
    try {
      await adminApi.put("/admin/kiosk-wall/config", {
        wall_id: wallId,
        grid, title, show_clock: showClock, show_weather_strip: showWeather,
        cells,
      });
      toast.success(`Saved · wall '${wallId}' live at /kiosk/wall${wallId !== "default" ? `?wall=${wallId}` : ""}`);
      reload(wallId);
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Live preview rendered with the same percentage CSS the kiosk uses
  const previewStyle = useMemo(() => ({
    display: "grid",
    gridTemplateColumns: `repeat(${dims.cols}, 1fr)`,
    gridTemplateRows: `repeat(${dims.rows}, 1fr)`,
    gap: "8px",
    aspectRatio: "16/9",
    width: "100%",
  }), [dims]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0c0d10] text-slate-900 dark:text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid="dispatch-video-wall-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider" data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4"/>Admin Console
            </Link>
            <div className="h-6 w-px bg-zinc-800"/>
            <MonitorPlay className="h-6 w-6 text-indigo-300"/>
            <h1 className="text-2xl font-extrabold tracking-tight">Dispatch Video Wall</h1>
            <Badge className="bg-indigo-500/20 text-indigo-200 border-indigo-400/40 text-[10px] uppercase tracking-widest">Developer · Kiosk</Badge>
          </div>
          <div className="flex gap-2">
            <Button onClick={reload} variant="ghost" className="text-zinc-300" data-testid="wall-reload"><RefreshCw className="h-4 w-4 mr-1.5"/>Reload</Button>
            <Button asChild variant="outline" className="border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/10 font-bold">
              <Link to="/admin/developer/kiosk-links" data-testid="open-kiosk-links">
                <Link2 className="h-4 w-4 mr-1.5"/>Provisioned Links
              </Link>
            </Button>
            <Button asChild className="bg-sky-500 text-white hover:bg-sky-400 font-bold">
              <a href="/kiosk/wall" target="_blank" rel="noreferrer" data-testid="wall-open-link">
                <ExternalLink className="h-4 w-4 mr-1.5"/>Open kiosk wall
              </a>
            </Button>
          </div>
        </div>

        {/* v2.7.62+E45 · §1 · Multi-instance wall switcher */}
        <Card className="bg-zinc-900 border-indigo-500/30 p-4 space-y-3" data-testid="wall-switcher-card">
          <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-extrabold pb-1 border-b border-indigo-400/40 flex items-center gap-2">
            <MonitorPlay className="h-3.5 w-3.5"/>Multi-instance topology · currently editing: {wallId}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Which wall are you editing?</label>
              <Select value={wallId} onValueChange={switchWall}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700" data-testid="wall-switcher-select"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700">
                  {walls.map((w) => (
                    <SelectItem key={w.wall_id} value={w.wall_id} data-testid={`wall-switcher-${w.wall_id}`}>
                      {w.wall_id === "default" ? "default (main)" : w.wall_id}
                      {w.title ? ` · ${w.title}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-zinc-500 mt-1">Each wall_id has a completely isolated config — changes here NEVER touch other walls.</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">New / target wall ID</label>
              <Input value={newWallId} onChange={(e) => setNewWallId(e.target.value)} placeholder="e.g. dispatch-room" className="bg-zinc-950 border-zinc-700" data-testid="wall-new-id-input" maxLength={64}/>
            </div>
            <div className="flex flex-col gap-1.5">
              <Button onClick={createWall} disabled={!newWallId.trim()} className="bg-indigo-500 text-white hover:bg-indigo-400 font-bold h-8 text-xs" data-testid="wall-create-btn">
                <Grid3x3 className="h-3.5 w-3.5 mr-1"/>Create empty
              </Button>
              <Button onClick={duplicateWall} disabled={!newWallId.trim()} className="bg-emerald-500 text-white hover:bg-emerald-400 font-bold h-8 text-xs" data-testid="wall-duplicate-btn" title={`Clone '${wallId}' to the ID typed above`}>
                <Save className="h-3.5 w-3.5 mr-1"/>Duplicate {wallId}
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-zinc-800">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
              {walls.length} isolated wall{walls.length === 1 ? "" : "s"} provisioned
            </div>
            <Button
              onClick={deleteWall}
              disabled={wallId === "default"}
              className="bg-red-600/80 text-white hover:bg-red-500 disabled:opacity-40 h-7 text-[10px] uppercase tracking-wider font-bold"
              data-testid="wall-delete-btn"
              title={wallId === "default" ? "Default wall is protected" : `Delete wall '${wallId}'`}
            >
              Delete &lsquo;{wallId}&rsquo;
            </Button>
          </div>
        </Card>

        {/* Settings */}
        <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-amber-400/40 flex items-center gap-2">
            <Grid3x3 className="h-3.5 w-3.5 text-amber-300"/>Wall settings
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Grid preset</label>
              <Select value={grid} onValueChange={onGridChange}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700" data-testid="wall-grid-select"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700">
                  {presets.map((p) => <SelectItem key={p} value={p} data-testid={`wall-grid-opt-${p}`}>{p} ({(GRID_DIMS[p] || {}).cols}×{(GRID_DIMS[p] || {}).rows})</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-zinc-500 mt-1">
                {isQuad
                  ? "9 semantic slots · 55%/45% split · 4K-optimized"
                  : `${expectedCount} cells · 16:9 widescreen · scales fluidly to 1080p & 4K`}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Wall title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-zinc-950 border-zinc-700" data-testid="wall-title-input" maxLength={80}/>
            </div>
            <div className="flex items-end gap-6 pb-1">
              <label className="flex items-center gap-2 text-xs text-zinc-200">
                <Switch checked={showClock} onCheckedChange={setShowClock} data-testid="wall-show-clock"/>Show clock
              </label>
              <label className="flex items-center gap-2 text-xs text-zinc-200">
                <Switch checked={showWeather} onCheckedChange={setShowWeather} data-testid="wall-show-weather"/>Weather strip
              </label>
            </div>
          </div>
        </Card>

        {/* Cells */}
        <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-sky-400/40">
            {isQuad ? `Quad topology · slot assignment (${cells.length}/9)` : `Cell assignment (${cells.length}/${expectedCount})`}
          </div>
          {isQuad ? (
            /* v2.7.62+E45 · §3 · Slot-addressed editor for 55-45-quad */
            <div className="space-y-2" data-testid="quad-slot-editor">
              {QUAD_SLOTS_ORDER.map((slot) => {
                const c = cells.find((x) => x.slot === slot) || { id: `slot-${slot}`, slot, ...QUAD_DEFAULTS[slot] };
                return (
                  <div key={slot} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr] gap-3 items-center" data-testid={`quad-slot-row-${slot}`}>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold">{slot}</div>
                      <div className="text-[10px] text-zinc-500">{QUAD_SLOT_LABELS[slot]}</div>
                    </div>
                    <Select value={c.type} onValueChange={(v) => setQuadSlotType(slot, v)}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-700 h-8 text-xs" data-testid={`quad-slot-${slot}-type`}><SelectValue/></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-700">
                        {Object.entries(cellTypes).map(([key, label]) => (
                          <SelectItem key={key} value={key} className="text-xs">{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {slot.startsWith("map_") && (
                      <Select value={c.zone || ""} onValueChange={(v) => setQuadSlotZone(slot, v)}>
                        <SelectTrigger className="bg-zinc-900 border-zinc-700 h-8 text-xs" data-testid={`quad-slot-${slot}-zone`}><SelectValue placeholder="Default zone"/></SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-700">
                          <SelectItem value="Z1" className="text-xs">Z1 · Weatherford</SelectItem>
                          <SelectItem value="Z2" className="text-xs">Z2 · Clinton</SelectItem>
                          <SelectItem value="Z3" className="text-xs">Z3 · Elk City</SelectItem>
                          <SelectItem value="Z4" className="text-xs">Z4 · Yukon</SelectItem>
                          <SelectItem value="Z5" className="text-xs">Z5 · OKC Metro</SelectItem>
                          <SelectItem value="Z6" className="text-xs">Z6 · Tulsa Metro</SelectItem>
                          <SelectItem value="Z7" className="text-xs">Z7 · Will Rogers</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {cells.map((c, idx) => (
                <div key={c.id || `cell-${idx}`} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3" data-testid={`wall-cell-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Cell {idx + 1}</div>
                    <Badge className="bg-zinc-900 border-zinc-700 text-zinc-400 text-[9px]">{c.id}</Badge>
                  </div>
                  <Select value={c.type} onValueChange={(v) => setCellType(idx, v)}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 h-8 text-xs" data-testid={`wall-cell-${idx}-select`}><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-700">
                      {Object.entries(cellTypes).map(([key, label]) => (
                        <SelectItem key={key} value={key} className="text-xs">{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={busy} className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold" data-testid="wall-save-btn">
              <Save className="h-4 w-4 mr-2"/>{busy ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </Card>

        {/* Preview */}
        {!isQuad && (
          <Card className="bg-zinc-900 border-zinc-800 p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-fuchsia-400/40 mb-3">
              Live preview · scales to 16:9 widescreen
            </div>
            <div style={previewStyle} data-testid="wall-preview-grid">
              {cells.map((c, idx) => (
                <div key={c.id || `preview-${idx}`} className="bg-zinc-950 border border-zinc-700 rounded-lg flex items-center justify-center text-center p-3" data-testid={`wall-preview-cell-${idx}`}>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Cell {idx + 1}</div>
                    <div className="text-sm font-bold text-zinc-200">{cellTypes[c.type] || c.type}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <SupervisorCard />
        <MacosLauncherCard />
        <DeploymentInstructions />
      </div>
    </div>
  );
}

// v2.7.60+E43 · §8 · Fixed Supervisor Command Footer — admin editable.
// The current on-duty supervisor name + phone renders across the bottom
// of every wall. This card is the single point of truth.
function SupervisorCard() {
  const [sup, setSup] = useState({ first_name: "", last_name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    adminApi.get("/admin/kiosk-wall/supervisor").then((r) => setSup(r.data || { first_name: "", last_name: "", phone: "" })).catch(() => {});
  }, []);
  const save = async () => {
    if (!sup.first_name.trim() || !sup.last_name.trim() || !sup.phone.trim()) {
      toast.error("First, Last, and Phone are required");
      return;
    }
    setBusy(true);
    try {
      await adminApi.put("/admin/kiosk-wall/supervisor", sup);
      toast.success("Supervisor footer updated · walls refresh within 60s");
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="supervisor-card">
      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-extrabold pb-1.5 border-b border-amber-400/40 flex items-center gap-2">
        <User className="h-3.5 w-3.5"/>Supervisor Command Footer (§8)
      </div>
      <p className="text-xs text-zinc-400">
        Renders fixed to the bottom-center of every wall as:{" "}
        <code className="text-amber-300">SUPERVISOR: [FIRST] [LAST] | [PHONE]</code>
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">First name</label>
          <Input value={sup.first_name} onChange={(e) => setSup({ ...sup, first_name: e.target.value })} className="bg-zinc-950 border-zinc-700" data-testid="sup-first-input" maxLength={40}/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Last name</label>
          <Input value={sup.last_name} onChange={(e) => setSup({ ...sup, last_name: e.target.value })} className="bg-zinc-950 border-zinc-700" data-testid="sup-last-input" maxLength={40}/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Phone</label>
          <Input value={sup.phone} onChange={(e) => setSup({ ...sup, phone: e.target.value })} placeholder="(580) 555-0100" className="bg-zinc-950 border-zinc-700" data-testid="sup-phone-input" maxLength={32}/>
        </div>
        <div className="flex items-end">
          <Button onClick={save} disabled={busy} className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold w-full" data-testid="sup-save-btn">
            <Save className="h-4 w-4 mr-2"/>{busy ? "Saving…" : "Save Supervisor"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// v2.7.60+E43 · §7 · macOS Launcher Packager card — one-click download of
// the .app bundle with the pre-generated COMS ICON.
function MacosLauncherCard() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const downloadZip = async () => {
    try {
      const res = await adminApi.get("/admin/kiosk-wall/macos-launcher", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = "BullerDispatchWall.app.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded BullerDispatchWall.app.zip");
    } catch (e) {
      toast.error(`Download failed: ${e?.response?.data?.detail || e.message}`);
    }
  };
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="macos-launcher-card">
      <div className="text-[10px] uppercase tracking-widest text-purple-300 font-extrabold pb-1.5 border-b border-purple-400/40 flex items-center gap-2">
        <Download className="h-3.5 w-3.5"/>macOS Launcher Packager (§7)
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
        <div className="flex items-center justify-center bg-zinc-950 border border-zinc-800 rounded-lg p-3" style={{ minWidth: 140, minHeight: 140 }}>
          <img
            src={`${API}/api/admin/kiosk-wall/icon.png?size=512&_=${Date.now()}`}
            alt="COMS ICON preview"
            style={{ width: 128, height: 128, imageRendering: "pixelated" }}
            data-testid="coms-icon-preview"
          />
        </div>
        <div className="space-y-3">
          <p className="text-xs text-zinc-300 leading-relaxed">
            One-click, no-code macOS launcher: click <span className="text-amber-300 font-bold">Download</span>,
            un-zip, drag <code className="text-amber-300">BullerDispatchWall.app</code> into <code className="text-amber-300">/Applications</code>,
            and launch. The bundle opens the Dispatch Wall in fullscreen kiosk mode (Chrome →
            Chromium → Safari fallback) with the custom{" "}
            <span className="text-purple-300 font-bold">COMS ICON</span> in the Dock. To pre-embed a
            token, provision a link on the{" "}
            <Link to="/admin/developer/kiosk-links" className="text-sky-300 underline">Provisioned Links</Link>{" "}
            page and use its &ldquo;macOS .app&rdquo; button.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={downloadZip} className="bg-purple-500 text-white hover:bg-purple-400 font-bold" data-testid="download-macos-launcher-btn">
              <Download className="h-4 w-4 mr-1.5"/>Download BullerDispatchWall.app.zip
            </Button>
            <Button asChild variant="ghost" className="text-zinc-300 hover:text-amber-300" data-testid="preview-icon-btn">
              <a href={`${API}/api/admin/kiosk-wall/icon.png?size=1024`} target="_blank" rel="noreferrer">
                <ImageIcon className="h-4 w-4 mr-1.5"/>Preview 1024×1024 icon
              </a>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// v2.7.35 · Step-by-step physical-deployment guide for the wall display.
// Kept inline so non-developer dispatch leads can copy/paste without
// digging through Confluence.
function DeploymentInstructions() {
  const publicBase = (typeof window !== "undefined") ? window.location.origin : "https://app.bullertaxi.com";
  const kioskUrl = `${publicBase}/kiosk/wall`;
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="wall-deployment-instructions">
      <div className="text-[11px] uppercase tracking-widest text-amber-300 font-extrabold pb-1.5 border-b border-amber-400/40 flex items-center gap-2">
        <MonitorPlay className="h-3.5 w-3.5"/>Deployment Instructions · Push to Wall
      </div>

      <p className="text-sm text-zinc-300 leading-relaxed">
        After you <span className="text-amber-300 font-bold">Save Changes</span>, the kiosk URL below renders the
        live wall layout. Point any always-on display at this URL — phone, tablet, Chromebox, Fire TV stick, Raspberry
        Pi, Windows mini-PC — and lock it into kiosk mode so the page can&rsquo;t be navigated away from.
      </p>

      <div className="bg-zinc-950 border border-zinc-700 rounded-lg p-3 flex items-center gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Kiosk URL</div>
        <code className="text-amber-200 text-xs font-mono break-all" data-testid="deploy-kiosk-url">{kioskUrl}</code>
        <Button
          type="button"
          variant="ghost"
          className="text-zinc-300 hover:text-amber-200 text-[10px] uppercase tracking-widest h-7 px-2"
          onClick={() => { navigator.clipboard?.writeText(kioskUrl); toast.success("Kiosk URL copied"); }}
          data-testid="deploy-copy-url"
        >Copy</Button>
      </div>

      {/* OS-specific deployment steps */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Chrome (any OS) */}
        <Card className="bg-zinc-950 border-zinc-800 p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold">Option A · Chrome Kiosk Mode (any OS · easiest)</div>
          <ol className="text-xs text-zinc-300 space-y-1.5 list-decimal pl-4">
            <li>Install <span className="text-amber-200 font-bold">Google Chrome</span> on the wall machine.</li>
            <li>Quit Chrome completely.</li>
            <li>Launch Chrome from a terminal / shortcut with this flag string:
              <div className="bg-zinc-900 border border-zinc-700 rounded p-2 mt-1 font-mono text-[11px] text-emerald-200 break-all">
                google-chrome --kiosk --noerrdialogs --disable-infobars --incognito {kioskUrl}
              </div>
              <span className="text-[10px] text-zinc-500">Windows binary: <code className="text-zinc-300">chrome.exe</code> · macOS: <code className="text-zinc-300">/Applications/Google\ Chrome.app/Contents/MacOS/Google\ Chrome</code></span>
            </li>
            <li>Set the OS to <span className="text-amber-200 font-bold">auto-login</span> + run the shortcut at startup.</li>
            <li>Disable system sleep/screensaver (Power Options &rarr; <span className="text-zinc-100">Never sleep</span>).</li>
          </ol>
        </Card>

        {/* Chromebox/Chromebook (managed) */}
        <Card className="bg-zinc-950 border-zinc-800 p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Option B · Chromebox / Chromebook (managed)</div>
          <ol className="text-xs text-zinc-300 space-y-1.5 list-decimal pl-4">
            <li>Enroll the device in your <a className="text-sky-300 underline" href="https://admin.google.com/" target="_blank" rel="noreferrer">Google Workspace</a> as a Kiosk.</li>
            <li>Device settings &rarr; Kiosks &rarr; <span className="text-zinc-100">Manage kiosk apps</span> &rarr; add <span className="text-amber-200">Kiosk &amp; Digital Signage</span> by <code className="text-zinc-300">Fugu Labs</code> (or any kiosk wrapper).</li>
            <li>Configure the kiosk app to launch <code className="text-emerald-200 break-all">{kioskUrl}</code> on boot.</li>
            <li>Set auto-relaunch on crash. Done — fully managed.</li>
          </ol>
        </Card>

        {/* Raspberry Pi */}
        <Card className="bg-zinc-950 border-zinc-800 p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">Option C · Raspberry Pi 4 (cheap · headless)</div>
          <ol className="text-xs text-zinc-300 space-y-1.5 list-decimal pl-4">
            <li>Flash <span className="text-amber-200 font-bold">Raspberry Pi OS (64-bit, Desktop)</span> &rarr; first boot, connect Wi-Fi.</li>
            <li>Install Chromium + unclutter:
              <div className="bg-zinc-900 border border-zinc-700 rounded p-2 mt-1 font-mono text-[11px] text-emerald-200">
                sudo apt update &amp;&amp; sudo apt install -y chromium-browser unclutter
              </div>
            </li>
            <li>Add to <code className="text-zinc-300">~/.config/lxsession/LXDE-pi/autostart</code>:
              <div className="bg-zinc-900 border border-zinc-700 rounded p-2 mt-1 font-mono text-[11px] text-emerald-200 break-all">
                @xset s noblank{"\n"}@xset s off{"\n"}@xset -dpms{"\n"}@unclutter -idle 0.5 -root{"\n"}@chromium-browser --kiosk --noerrdialogs --disable-infobars --incognito {kioskUrl}
              </div>
            </li>
            <li>Reboot. Wall display is now permanent + auto-recovers on power loss.</li>
          </ol>
        </Card>

        {/* Fire TV / Android TV */}
        <Card className="bg-zinc-950 border-zinc-800 p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">Option D · Fire TV Stick / Android TV</div>
          <ol className="text-xs text-zinc-300 space-y-1.5 list-decimal pl-4">
            <li>Install <span className="text-amber-200 font-bold">Fully Kiosk Browser</span> (Amazon Appstore / Play Store).</li>
            <li>Open Fully Kiosk &rarr; Settings &rarr; Start URL &rarr; paste <code className="text-emerald-200 break-all">{kioskUrl}</code></li>
            <li>Enable <span className="text-zinc-100">Auto-Start on Boot</span> + <span className="text-zinc-100">Keep Screen On</span> + <span className="text-zinc-100">Restart on Crash</span>.</li>
            <li>Set a 4-digit PIN to lock out the settings menu.</li>
            <li>Reboot to verify.</li>
          </ol>
        </Card>
      </div>

      {/* Wall + display recommendations */}
      <Card className="bg-zinc-950 border-zinc-800 p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Hardware notes</div>
        <ul className="text-xs text-zinc-300 space-y-1 list-disc pl-4">
          <li><span className="text-zinc-100 font-bold">Display:</span> any 1080p TV or commercial-grade signage panel. The layout is responsive — 1080p, 4K, and ultrawide all work.</li>
          <li><span className="text-zinc-100 font-bold">Aspect ratio:</span> the layout snaps to 16:9. For vertical / portrait displays, choose the <code className="text-amber-200">2x1</code> or <code className="text-amber-200">1x1</code> grid.</li>
          <li><span className="text-zinc-100 font-bold">Network:</span> hardwired Ethernet is strongly preferred over Wi-Fi for 24/7 dispatch reliability.</li>
          <li><span className="text-zinc-100 font-bold">Power:</span> connect to a UPS so the wall survives short outages. Plug the display + computer + router into the SAME UPS.</li>
          <li><span className="text-zinc-100 font-bold">Authentication:</span> the kiosk URL is <span className="text-amber-200">public read-only</span> by design (no operator login required) — the wall shows live status but cannot dispatch trips, ground drivers, or accept payments. Mount the screen in a secure area.</li>
        </ul>
      </Card>

      {/* Troubleshooting */}
      <Card className="bg-zinc-950 border-zinc-800 p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-rose-300 font-bold">Troubleshooting</div>
        <ul className="text-xs text-zinc-300 space-y-1.5 list-disc pl-4">
          <li><span className="text-zinc-100 font-bold">Blank / black screen:</span> open the URL on your laptop first. If it loads there, the wall device is the issue (network / browser version).</li>
          <li><span className="text-zinc-100 font-bold">Cells show &ldquo;loading&rdquo; forever:</span> the wall machine cannot reach <code className="text-zinc-300 break-all">{publicBase}/api/kiosk/wall</code>. Check DNS + firewall (port 443 outbound must be open).</li>
          <li><span className="text-zinc-100 font-bold">Layout looks cramped:</span> change the Grid preset above to one with fewer cells — 2x2 / 3x2 for &lt; 1080p, 3x3 / 4x2 for 4K.</li>
          <li><span className="text-zinc-100 font-bold">Wall went stale:</span> the kiosk reloads its config every 60 s. Force-reload with <code className="text-zinc-300">Ctrl+F5</code> (or just power-cycle the display).</li>
          <li><span className="text-zinc-100 font-bold">Screen turns off after N minutes:</span> disable OS power-management <em>and</em> the display&rsquo;s own auto-off menu (usually in the TV&rsquo;s System / Eco settings).</li>
        </ul>
      </Card>

      <p className="text-[10px] text-zinc-500 italic">
        Last updated v2.7.35 · 2026-02-21. For questions ping <span className="text-zinc-300">#dispatch-eng</span>.
      </p>
    </Card>
  );
}
